/**
 * Cross-platform fetch: native fetch when available (browser / Node 18+),
 * else Node.js `https` fallback (Node < 18).
 */

import DerivHttpError from './DerivHttpError';

/**
 * Reject `.json()` with full HTTP context instead of JSON.parse opaque failures.
 *
 * @param {string} requestUrl
 * @param {string} bodyText
 * @param {number} status
 * @param {string} statusText
 * @param {unknown} parseError
 */
const throwDerivHttpBadJson = (requestUrl, bodyText, status, statusText, parseError) => {
    throw new DerivHttpError({
        url: requestUrl,
        status,
        statusText,
        bodyText,
        parseError,
    });
};

/**
 * @param {*} response Fetch API Response (native).
 * @param {string} requestUrl
 */
const wrapNativeResponse = (response, requestUrl) => {
    /** @type {string | null} */
    let cache = null;
    const readText = async () => {
        if (cache === null) {
            cache = await response.text();
        }
        return cache;
    };

    return {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        text: readText,
        json: async () => {
            const bodyText = await readText();
            try {
                return JSON.parse(bodyText);
            } catch (parseError) {
                throwDerivHttpBadJson(requestUrl, bodyText, response.status, response.statusText, parseError);
            }
        },
    };
};

/**
 * Shared entry: returns a Fetch-shaped object with caching `json`/`text` on both paths.
 * @param {RequestInfo | URL | string} url
 * @param {RequestInit & { timeout?: number }} [options]
 */
export const internalFetch = async (url, options = {}) => {
    const requestUrl = typeof url === 'string' ? url : String(url);

    const globalScope =
        typeof globalThis !== 'undefined'
            ? globalThis
            : typeof window !== 'undefined'
              ? window
              : typeof global !== 'undefined'
                ? global
                : {};

    if (typeof globalScope.fetch === 'function') {
        // console.log('[deriv-live-api-v2] Using native fetch');
        const response = await globalScope.fetch(url, options);
        return wrapNativeResponse(response, requestUrl);
    }

    // console.log('[deriv-live-api-v2] Native fetch not found, attempting Node.js fallback');

    const isNode = typeof process !== 'undefined' && process.versions && process.versions.node;
    if (!isNode) {
        throw new Error('fetch is not defined in this environment. Please provide a polyfill.');
    }

    let https;
    try {
        https = eval('require("https")');
    } catch (e) {
        throw new Error('Failed to load Node.js https module for fetch fallback.');
    }

    let URL;
    try {
        URL = eval('require("url")').URL;
    } catch (e) {
        // URL ctor below will fail if unavailable
    }

    return new Promise((resolve, reject) => {
        const urlObj = new URL(url);
        const headers = { ...options.headers };
        let body = options.body;

        if (
            body &&
            typeof body.toString === 'function' &&
            (body instanceof URLSearchParams || body.constructor.name === 'URLSearchParams')
        ) {
            body = body.toString();
            if (!headers['Content-Type']) {
                headers['Content-Type'] = 'application/x-www-form-urlencoded';
            }
        }

        const timeoutMs = options.timeout || 30000;

        const requestOptions = {
            method: (options.method || 'GET').toUpperCase(),
            headers,
            hostname: urlObj.hostname,
            path: urlObj.pathname + urlObj.search,
            port: urlObj.port || (urlObj.protocol === 'https:' ? 443 : 80),
            timeout: timeoutMs,
        };

        const req = https.request(requestOptions, (res) => {
            let responseData = '';
            res.on('data', (chunk) => {
                responseData += chunk;
            });
            res.on('end', () => {
                const status = res.statusCode || 0;
                const statusText = res.statusMessage || '';
                const bodyText = responseData;

                /** @type {string | null} */
                let cache = bodyText;

                const readText = async () => (cache == null ? '' : cache);

                resolve({
                    ok: status >= 200 && status < 300,
                    status,
                    statusText,
                    text: readText,
                    json: async () => {
                        const text = await readText();
                        try {
                            return JSON.parse(text);
                        } catch (parseError) {
                            throwDerivHttpBadJson(requestUrl, text, status, statusText, parseError);
                        }
                    },
                    headers: {
                        get: (name) => res.headers[name.toLowerCase()],
                    },
                });
            });

            res.on('error', (err) => {
                reject(err);
            });
        });

        req.on('timeout', () => {
            req.destroy();
            reject(new Error(`Request timed out after ${timeoutMs}ms`));
        });

        req.on('error', (err) => {
            reject(err);
        });

        if (body) {
            req.write(body);
        }
        req.end();
    });
};
